import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Compass,
  HeartPulse,
  Info,
  LayoutDashboard,
  LogOut,
  Moon,
  Settings,
  Sparkles,
  Sun,
  UserPlus,
  Watch,
} from '@/utils/icons';
import { useTheme } from '@/contexts/ThemeContext';
import { useModals } from '@/contexts/ModalContext';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { useTutorials } from '@/contexts/TutorialsContext';
import { useSidebar } from '@/contexts/SidebarContext';
import SidebarRailItem from './SidebarRailItem';

interface UserInfo {
  username: string;
  role?: string;
  full_name?: string;
  first_name?: string;
  team_name?: string;
  phone?: string;
}

interface NavOption {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  iconImage?: { light: string; dark: string };
  onClick?: () => void;
  /** Optional `data-tour` anchor slug for guided tutorials. */
  dataTour?: string;
  /** Optional count badge on the icon. */
  badgeCount?: number;
}

const readUserInfo = (): UserInfo | null => {
  try {
    const raw = localStorage.getItem('user_info');
    if (!raw) return null;
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SidebarRail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { openInviteUserModal } = useModals();
  const { openTutorials, activeTutorial } = useTutorials();
  const { isCollapsed, toggleCollapsed, isMobileDrawerOpen, openMobileDrawer, closeMobileDrawer } = useSidebar();
  const agentSmsEnabled = useFeatureFlag('agent_sms_v2');
  const calendarEnabled = useFeatureFlag('calendar_v1');

  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => readUserInfo());

  // Re-read userInfo on route change and after phone/profile saves.
  useEffect(() => {
    setUserInfo(readUserInfo());
  }, [location.pathname]);

  useEffect(() => {
    const onProfileUpdate = () => setUserInfo(readUserInfo());
    window.addEventListener('user-info-changed', onProfileUpdate);
    return () => window.removeEventListener('user-info-changed', onProfileUpdate);
  }, []);

  // While a guided tour needs a sidebar target (e.g. theme toggle), make sure
  // the rail is visible: a collapsed rail or closed mobile drawer pushes those
  // items off-canvas via CSS transform. We only open the mobile drawer on
  // actual mobile viewports — opening it on desktop left `isMobileDrawerOpen`
  // true after the tour ended, which kept `document.body` scroll-locked until
  // the next navigation. Cleanup closes the drawer only if this effect opened it.
  useEffect(() => {
    if (!activeTutorial) return;
    const needsSidebar = activeTutorial.steps.some((s) => s.target.startsWith('sidebar-'));
    if (!needsSidebar) return;

    if (isCollapsed) toggleCollapsed();
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 768px)').matches;
    let openedDrawer = false;
    if (isMobile) {
      openMobileDrawer();
      openedDrawer = true;
    }
    return () => {
      if (openedDrawer) closeMobileDrawer();
    };
    // Only react to a tour starting/stopping, not to every collapse toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTutorial]);

  const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('access');

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavigate = (path: string) => navigate(path);

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user_info');
    navigate('/login');
  };

  const { topNav, bottomNav } = useMemo(() => {
    const role = userInfo?.role;
    const top: NavOption[] = [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ];
    if (role === 'athlete') {
      top.push({
        id: 'daily-checkin',
        label: 'Daily Check-In',
        path: '/daily-checkin/today',
        icon: ClipboardList,
      });
    }
    if (role === 'admin') {
      top.push({
        id: 'admin-dashboard',
        label: 'Admin Dashboard',
        path: '/admin-dashboard',
        icon: Settings,
      });
    }
    top.push({
      id: 'team-information',
      label: 'Team Information',
      path: '/team-information',
      icon: Info,
    });
    if (role === 'coach' || role === 'admin') {
      top.push({
        id: 'team-health',
        label: 'Team Health',
        path: '/team-health',
        icon: HeartPulse,
      });
    }
    if (agentSmsEnabled && (role === 'coach' || role === 'admin')) {
      top.push({ id: 'agent', label: 'Agent Inbox', path: '/agent', icon: Sparkles });
    }
    if (role === 'coach' || role === 'admin') {
      top.push({
        id: 'catapult',
        label: 'Catapult',
        path: '/catapult-dashboard',
        icon: LayoutDashboard,
        iconImage: {
          light: '/logos/catapult/catapult-icon-black.webp',
          dark: '/logos/catapult/catapult-icon-white.webp',
        },
      });
      top.push({
        id: 'hawkin',
        label: 'Hawkin Dynamics',
        path: '/hawkin-dashboard',
        icon: LayoutDashboard,
        iconImage: {
          light: '/logos/hawkin/hawkin-icon-black.webp',
          dark: '/logos/hawkin/hawkin-icon-white.webp',
        },
      });
    }
    if (calendarEnabled) {
      top.push({
        id: 'calendar',
        label: 'Calendar',
        path: '/calendar',
        icon: Calendar,
      });
    }
    top.push({
      id: 'connect-wearable',
      label: 'Connect to Wearable',
      path: '/connect-wearable',
      icon: Watch,
    });
    if (role === 'coach' || role === 'admin') {
      top.push({
        id: 'invite',
        label: 'Invite a New User',
        path: '#invite',
        icon: UserPlus,
        dataTour: 'sidebar-invite-user',
        onClick: () => {
          closeMobileDrawer();
          openInviteUserModal();
        },
      });
    }

    const needsPhone = !userInfo?.phone;
    const bottom: NavOption[] = [
      { id: 'settings', label: 'Settings', path: '/settings', icon: Settings, badgeCount: needsPhone ? 1 : undefined },
    ];

    return { topNav: top, bottomNav: bottom };
  }, [userInfo?.role, userInfo?.phone, agentSmsEnabled, calendarEnabled, openInviteUserModal, closeMobileDrawer]);

  if (!isAuthenticated) return null;

  const teamName = userInfo?.team_name || 'CGM Sports';
  const identityName = userInfo?.full_name || userInfo?.first_name || userInfo?.username || '';

  return (
    <aside
      className={`cgm-rail ${isCollapsed ? 'cgm-rail--collapsed' : 'cgm-rail--expanded'}${isMobileDrawerOpen ? ' cgm-rail--mobile-open' : ''}`}
      aria-label="Primary navigation"
    >
      <div className="cgm-rail-header">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="cgm-rail-brand"
          aria-label="Go to dashboard"
        >
          <img
            src={isDark ? '/logos/FAVICON-WHITE.png' : '/logos/FAVICON-BLK.png'}
            alt="CGM Sports"
            className="cgm-rail-logo"
          />
          <span className="cgm-rail-team" aria-hidden={isCollapsed}>{teamName}</span>
        </button>
        <button
          type="button"
          onClick={toggleCollapsed}
          className="cgm-rail-collapse-toggle"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <nav className="cgm-rail-nav">
        {topNav.map((item) => (
          <SidebarRailItem
            key={item.id}
            label={item.label}
            icon={item.icon}
            iconImage={item.iconImage}
            isDark={isDark}
            isActive={isActive(item.path)}
            isCollapsed={isCollapsed}
            onClick={() => (item.onClick ? item.onClick() : handleNavigate(item.path))}
            dataTour={item.dataTour}
          />
        ))}
      </nav>

      <div className="cgm-rail-footer">
        <SidebarRailItem
          label="Tutorials"
          icon={Compass}
          isCollapsed={isCollapsed}
          onClick={() => {
            closeMobileDrawer();
            openTutorials();
          }}
          dataTour="sidebar-tutorials"
        />
        {bottomNav.map((item) => (
          <SidebarRailItem
            key={item.id}
            label={item.label}
            icon={item.icon}
            isActive={isActive(item.path)}
            isCollapsed={isCollapsed}
            onClick={() => handleNavigate(item.path)}
            badgeCount={item.badgeCount}
          />
        ))}
        <SidebarRailItem
          label={isDark ? 'Light Mode' : 'Dark Mode'}
          icon={isDark ? Sun : Moon}
          isCollapsed={isCollapsed}
          onClick={toggleTheme}
          dataTour="sidebar-theme-toggle"
        />
        <SidebarRailItem
          label="Logout"
          icon={LogOut}
          isCollapsed={isCollapsed}
          onClick={handleLogout}
          variant="danger"
        />

        {identityName && (
          <div className="cgm-rail-identity" aria-hidden={isCollapsed}>
            <div className="cgm-rail-avatar" aria-hidden>
              {getInitials(identityName)}
            </div>
            <div className="cgm-rail-identity-name">{identityName}</div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default SidebarRail;
